import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  slackService,
  NotificationTemplate,
  UpdateNotificationTemplateDto,
} from '../services/slackService';

export const notificationTemplateKeys = {
  all: ['notificationTemplates'] as const,
  list: () => [...notificationTemplateKeys.all, 'list'] as const,
};

/**
 * Hook: useNotificationTemplates
 * Fetch the organization's editable notification message templates.
 * `enabled` gates the request so the admin-only endpoint is not called
 * for non-admins (defaults to enabled).
 */
export const useNotificationTemplates = (enabled = true) => {
  return useQuery<NotificationTemplate[]>({
    queryKey: notificationTemplateKeys.list(),
    queryFn: () => slackService.getTemplates(),
    enabled,
    staleTime: 0,
    refetchOnMount: true,
  });
};

/**
 * Hook: useUpdateNotificationTemplates
 * Update one or more notification templates and refresh the cache.
 */
export const useUpdateNotificationTemplates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templates: UpdateNotificationTemplateDto[]) =>
      slackService.updateTemplates(templates),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationTemplateKeys.list(), data);
      queryClient.invalidateQueries({ queryKey: notificationTemplateKeys.all });
    },
  });
};
